import React, { useState } from 'react';
import './ConfigurationPanel.css';
import { isLayoutAvailable, isDoubleDrumAvailable } from './productRules';
import PowerOptionsSelector from './PowerOptionsSelector';
import SelectJudet from './SelectJudet';

const ConfigurationPanel = ({
  config,
  selectedProduct,
  onFractionChange,
  onLayoutChange,
  onAccessoryChange,
  onDetailsChange,
  onFractionDrumChange,
  onPowerOptionsChange,
  onHookTypeChange,
  onFractionQuantityChange,
  onFractionVolumeChange,
  // handler pentru județ (obligatoriu acum)
  onJudetChange,
  // eroare pentru județ (din ProductConfigurator)
  judetError,
}) => {
  // State to track which sections are expanded
  const [expandedSections, setExpandedSections] = useState({
    fractions: true,
    power: true,
    volume: true,
    layout: true,
    accessories: true,
    hookType: true,
    region: true,
  });

  // Toggle section visibility
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  // Helper function to render drum type options based on fraction
  const renderDrumOptions = (fractionType) => {
    const canHaveDoubleDrum = isDoubleDrumAvailable(selectedProduct, fractionType);

    return (
      <div className="drum-type-selector">
        <label className="option-item radio">
          <input
            type="radio"
            name={`${fractionType}DrumType`}
            value="single"
            checked={config.fractions[fractionType].drumType === 'single'}
            onChange={() => onFractionDrumChange(fractionType, 'single')}
          />
          <span className="option-text">Tambur simplu</span>
        </label>

        {canHaveDoubleDrum && (
          <label className="option-item radio">
            <input
              type="radio"
              name={`${fractionType}DrumType`}
              value="double"
              checked={config.fractions[fractionType].drumType === 'double'}
              onChange={() => onFractionDrumChange(fractionType, 'double')}
            />
            <span className="option-text">Tambur dublu</span>
          </label>
        )}
      </div>
    );
  };

  // Helper function to render quantity selector for each enabled fraction
  const renderQuantitySelector = (fractionType) => {
    return (
      <div className="quantity-selector">
        <label className="quantity-label">
          <span className="option-text">Cantitate:</span>
          <input
            type="number"
            min="1"
            value={config.fractions[fractionType].quantity || 1}
            onChange={(e) => onFractionQuantityChange(fractionType, e.target.value)}
            className="quantity-input"
          />
        </label>
      </div>
    );
  };

  // Helper function to render volume selector for each fraction (for tip2 only)
  const renderVolumeSelector = (fractionType) => {
    // Only show volume selector for tip2
    if (selectedProduct !== 'tip2') return null;

    // For residual, biodegradable, and glass - show locked radio button with fixed 3000L
    if (fractionType === 'residual' || fractionType === 'biodegradable' || fractionType === 'glass') {
      return (
        <div className="volume-selector">
          <div className="volume-title">Volum:</div>
          <div className="volume-options">
            <div className="volume-option-container">
              <label className="option-item radio locked">
                <input
                  type="radio"
                  name={`${fractionType}Volume`}
                  value="3000"
                  checked={true}
                  disabled={true}
                />
                <span className="option-text">
                  3000L (3mc){' '}
                  <span className="locked-label">
                    (această fracție nu permite un volum mai mare)
                  </span>
                </span>
              </label>
              <span className="volume-description">100 aruncari - 30L/aruncare</span>
            </div>
          </div>
        </div>
      );
    }

    // For plastic and paper, show selectable volume options
    if (fractionType === 'plastic' || fractionType === 'paper') {
      return (
        <div className="volume-selector">
          <div className="volume-title">Volum:</div>
          <div className="volume-options">
            <div className="volume-option-container">
              <label className="option-item radio">
                <input
                  type="radio"
                  name={`${fractionType}Volume`}
                  value="3000"
                  checked={(config.fractions[fractionType].volume || '3000') === '3000'}
                  onChange={() => onFractionVolumeChange(fractionType, '3000')}
                />
                <span className="option-text">3000L (3mc)</span>
              </label>
              <span className="volume-description">100 aruncari - 30L/aruncare</span>
            </div>

            <div className="volume-option-container">
              <label className="option-item radio">
                <input
                  type="radio"
                  name={`${fractionType}Volume`}
                  value="5000"
                  checked={(config.fractions[fractionType].volume || '3000') === '5000'}
                  onChange={() => onFractionVolumeChange(fractionType, '5000')}
                />
                <span className="option-text">5000L (5mc)</span>
              </label>
              <span className="volume-description">166 aruncari - 30L/aruncare</span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Check if layouts should be shown
  const showLayouts = selectedProduct === 'tip1';

  // Check if hook type should be shown (only for tip2 and tip3)
  const showHookType = selectedProduct === 'tip2' || selectedProduct === 'tip3';

  // Get the count of enabled fractions
  const enabledFractionCount = Object.values(config.fractions).filter(
    (fraction) => fraction.enabled
  ).length;

  return (
    <div className="configuration-panel">
      <h2>Opțiuni configurare</h2>

      {/* Secțiunea Fracții cu opțiuni de tambur pentru fiecare fracție */}
      <div className="config-section">
        <div
          className="section-header collapsible"
          onClick={() => toggleSection('fractions')}
        >
          <h3>
            Fracții{' '}
            <span className="fraction-count">({enabledFractionCount} selectate)</span>
          </h3>
          <span className="toggle-icon-configuration">
            {expandedSections.fractions ? '▼' : '►'}
          </span>
        </div>

        {expandedSections.fractions && (
          <div className="option-list fraction-grid">
            {/* Fracție rezidual */}
            <div className="fraction-item">
              <label className="option-item checkbox">
                <input
                  type="checkbox"
                  checked={config.fractions.residual.enabled}
                  onChange={(e) =>
                    onFractionChange('residual', 'enabled', e.target.checked)
                  }
                />
                <span className="option-text">Rezidual</span>
              </label>

              {config.fractions.residual.enabled && (
                <div className="fraction-details">
                  {renderDrumOptions('residual')}
                  {renderVolumeSelector('residual')}
                  {renderQuantitySelector('residual')}
                </div>
              )}
            </div>

            {/* Fracție biodegradabil */}
            <div className="fraction-item">
              <label className="option-item checkbox">
                <input
                  type="checkbox"
                  checked={config.fractions.biodegradable.enabled}
                  onChange={(e) =>
                    onFractionChange('biodegradable', 'enabled', e.target.checked)
                  }
                />
                <span className="option-text">Biodegradabil</span>
              </label>

              {config.fractions.biodegradable.enabled && (
                <div className="fraction-details">
                  {renderDrumOptions('biodegradable')}
                  {renderVolumeSelector('biodegradable')}
                  {renderQuantitySelector('biodegradable')}
                </div>
              )}
            </div>

            {/* Fracție plastic */}
            <div className="fraction-item">
              <label className="option-item checkbox">
                <input
                  type="checkbox"
                  checked={config.fractions.plastic.enabled}
                  onChange={(e) =>
                    onFractionChange('plastic', 'enabled', e.target.checked)
                  }
                />
                <span className="option-text">Plastic/Metal</span>
              </label>

              {config.fractions.plastic.enabled && (
                <div className="fraction-details">
                  {renderDrumOptions('plastic')}
                  {renderVolumeSelector('plastic')}
                  {renderQuantitySelector('plastic')}
                </div>
              )}
            </div>

            {/* Fracție hârtie */}
            <div className="fraction-item">
              <label className="option-item checkbox">
                <input
                  type="checkbox"
                  checked={config.fractions.paper.enabled}
                  onChange={(e) =>
                    onFractionChange('paper', 'enabled', e.target.checked)
                  }
                />
                <span className="option-text">Hârtie/Carton</span>
              </label>

              {config.fractions.paper.enabled && (
                <div className="fraction-details">
                  {renderDrumOptions('paper')}
                  {renderVolumeSelector('paper')}
                  {renderQuantitySelector('paper')}
                </div>
              )}
            </div>

            {/* Fracție sticlă */}
            <div className="fraction-item">
              <label className="option-item checkbox">
                <input
                  type="checkbox"
                  checked={config.fractions.glass.enabled}
                  onChange={(e) =>
                    onFractionChange('glass', 'enabled', e.target.checked)
                  }
                />
                <span className="option-text">Sticlă</span>
              </label>

              {config.fractions.glass.enabled && (
                <div className="fraction-details">
                  {renderDrumOptions('glass')}
                  {renderVolumeSelector('glass')}
                  {renderQuantitySelector('glass')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Secțiunea Acces Digitizat/Nedigitizat */}
      <div className="config-section">
        <div
          className="section-header collapsible"
          onClick={() => toggleSection('power')}
        >
          <h3>Acces Digitizat/Nedigitizat</h3>
          <span className="toggle-icon-configuration">
            {expandedSections.power ? '▼' : '►'}
          </span>
        </div>

        {expandedSections.power && (
          <PowerOptionsSelector
            selectedModel={selectedProduct}
            config={{
              powerType: config.powerType || 'electric',
              electricOption: config.electricOption || '',
              accessOption: config.accessOption || '',
            }}
            onChange={onPowerOptionsChange}
          />
        )}
      </div>

      {/* Secțiunea Așezare - doar pentru tip1 */}
      {showLayouts && (
        <div className="config-section">
          <div
            className="section-header collapsible"
            onClick={() => toggleSection('layout')}
          >
            <h3>Așezare</h3>
            <span className="toggle-icon-configuration">
              {expandedSections.layout ? '▼' : '►'}
            </span>
          </div>

          {expandedSections.layout && (
            <div className="option-list layout-grid">
              <label className="option-item radio">
                <input
                  type="radio"
                  name="layout"
                  value="linear"
                  checked={config.layout === 'linear'}
                  onChange={() => onLayoutChange('linear')}
                />
                <span className="option-text">Liniară</span>
              </label>

              <label className="option-item radio">
                <input
                  type="radio"
                  name="layout"
                  value="L-shape"
                  checked={config.layout === 'L-shape'}
                  onChange={() => onLayoutChange('L-shape')}
                />
                <span className="option-text">Tip L</span>
              </label>

              <label className="option-item radio">
                <input
                  type="radio"
                  name="layout"
                  value="U-shape"
                  checked={config.layout === 'U-shape'}
                  onChange={() => onLayoutChange('U-shape')}
                />
                <span className="option-text">Tip U</span>
              </label>

              <label className="option-item radio">
                <input
                  type="radio"
                  name="layout"
                  value="back-to-back"
                  checked={config.layout === 'back-to-back'}
                  onChange={() => onLayoutChange('back-to-back')}
                />
                <span className="option-text">Spate în spate</span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Secțiunea Tip Cârlig - doar pentru tip2 și tip3 */}
      {showHookType && (
        <div className="config-section">
          <div
            className="section-header collapsible"
            onClick={() => toggleSection('hookType')}
          >
            <h3>Tip Cârlig</h3>
            <span className="toggle-icon-configuration">
              {expandedSections.hookType ? '▼' : '►'}
            </span>
          </div>

          {expandedSections.hookType && (
            <div className="option-list hook-type-grid">
              <label className="option-item radio hook-option">
                <input
                  type="radio"
                  name="hookType"
                  value="simplu"
                  checked={config.hookType === 'simplu'}
                  onChange={() => onHookTypeChange('simplu')}
                />
                <span className="option-text">Cârlig Simplu</span>
                <img
                  src="/images/hooks/simplu-carlig.png"
                  alt="Simplu cârlig"
                  className="hook-type-image"
                />
              </label>

              <label className="option-item radio hook-option">
                <input
                  type="radio"
                  name="hookType"
                  value="dublu-linie"
                  checked={config.hookType === 'dublu-linie'}
                  onChange={() => onHookTypeChange('dublu-linie')}
                />
                <span className="option-text">Dublu cârlig în linie</span>
                <img
                  src="/images/hooks/dublu-carlig-linie.png"
                  alt="Dublu cârlig în linie"
                  className="hook-type-image"
                />
              </label>

              <label className="option-item radio hook-option">
                <input
                  type="radio"
                  name="hookType"
                  value="dublu-paralel"
                  checked={config.hookType === 'dublu-paralel'}
                  onChange={() => onHookTypeChange('dublu-paralel')}
                />
                <span className="option-text">Dublu cârlig în paralel</span>
                <img
                  src="/images/hooks/dublu-carlig-paralel.png"
                  alt="Dublu cârlig în paralel"
                  className="hook-type-image"
                />
              </label>

              <label className="option-item radio hook-option">
                <input
                  type="radio"
                  name="hookType"
                  value="ciuperca"
                  checked={config.hookType === 'ciuperca'}
                  onChange={() => onHookTypeChange('ciuperca')}
                />
                <span className="option-text">Cârlig tip Ciupercă</span>
                <img
                  src="/images/hooks/carlig-ciuperca.png"
                  alt="Cârlig tip Ciupercă"
                  className="hook-type-image"
                />
              </label>
            </div>
          )}
        </div>
      )}

      {/* Secțiunea Regiune / Județ */}
      <div className="config-section">
        <div
          className="section-header collapsible"
          onClick={() => toggleSection('region')}
        >
          <h3>Regiune / Județ</h3>
          <span className="toggle-icon-configuration">
            {expandedSections.region ? '▼' : '►'}
          </span>
        </div>

        {expandedSections.region && (
          <div className="option-list">
            <SelectJudet
              value={config.judet}
              onChange={onJudetChange}
              required={true}
              error={!config.judet ? "Selectarea județului este obligatorie" : null}
            />
          </div>
        )}
      </div>

      {/* Secțiunea Accesorii */}
      <div className="config-section">
        <div
          className="section-header collapsible"
          onClick={() => toggleSection('accessories')}
        >
          <h3>Accesorii</h3>
          <span className="toggle-icon-configuration">
            {expandedSections.accessories ? '▼' : '►'}
          </span>
        </div>

        {expandedSections.accessories && (
          <div className="option-list">
            <label className="option-item checkbox">
              <input
                type="checkbox"
                checked={config.accessories?.colantare || false}
                onChange={(e) => onAccessoryChange('colantare', e.target.checked)}
              />
              <span className="option-text">Colantare</span>
            </label>
          </div>
        )}
      </div>

      {/* Câmp pentru detalii suplimentare */}
      <div className="config-section">
        <div className="details-field">
          <label>
            <span className="details-label">Adaugă detalii suplimentare:</span>
            <textarea
              value={config.additionalDetails || ''}
              onChange={(e) => onDetailsChange(e.target.value)}
              placeholder="Scrieți aici..."
              rows={8}
              className="details-input"
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPanel;
